import { fetchOffersById } from 'actions/OfferActions'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import HighIntentRecommendationTile from 'components/OfferList/OfferListTiles/HighIntent/HighIntentRecommendationTile'
import { getYMALViewForOffer } from 'components/Recommendations/YouMayAlsoLike/Shared/YouMayAlsoLikeOffersCarousel'
import useTrackOfferImpressions from 'components/Recommendations/hooks/useTrackOfferImpressions'
import GeoContext from 'contexts/geoContext'
import { nonNullable } from 'lib/arrayUtils'
import uuidV4 from 'lib/uuidV4'
import rem from 'polished/lib/helpers/rem'
import React, { useContext, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { isRecentlyViewedOffersLoading, getRecentlyViewedOfferIds, getRecentlyViewedOffers } from 'selectors/recentViewedOffers'
import type { AppDispatch } from 'src/client/store'
import styled from 'styled-components'
import useIsMobile from 'tripPlanner/hooks/responsive/useIsMobile'

const Section = styled(Pane)`
  padding: ${rem(16)} 0;
`

interface MappedProps {
  offers: Array<App.Offer | App.OfferSummary>;
  recentlyViewedOfferIds: Array<string>;
  defaultDepositAmountPercentage: number;
  isRecentlyViewedLoading: boolean;
}

interface Props {
}

function RecentlyViewedOffersCarousel(props: Props & MappedProps) {
  const {
    offers,
    recentlyViewedOfferIds,
    defaultDepositAmountPercentage,
    isRecentlyViewedLoading,
  } = props
  const dispatch = useDispatch<AppDispatch>()

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)

  useEffect(() => {
    dispatch(fetchOffersById(recentlyViewedOfferIds))
  }, [dispatch, recentlyViewedOfferIds])

  const tracking = useMemo(() => ({
    category: 'Recently Viewed Offers',
    listName: 'Recently Viewed Offers',
    listId: uuidV4(),
    label: '',
  }), [])

  const offerViews = useMemo(() => {
    return nonNullable(offers.map((offer, index) => {
      return getYMALViewForOffer(offer, {
        testId: 'ymal-tile-condensed',
        position: index + 1,
        currency: currentCurrency,
        tracking,
        defaultDepositAmountPercentage,
        currentRegionCode,
      })
    }))
  }, [currentCurrency, currentRegionCode, offers, tracking, defaultDepositAmountPercentage])

  useTrackOfferImpressions(
    offers,
    tracking,
    offers.length > 0,
    currentCurrency,
  )

  const isMobile = useIsMobile()
  const tileGap = isMobile ? 16 : 4

  if (offers.length === 0 || isRecentlyViewedLoading) {
    return null
  }

  return <Section>
    <LayoutContainer>
      <VerticalSpacer gap={16}>
        <BodyText variant="medium" weight="bold">Still interested in these offers?</BodyText>
        <CardCarousel gap={tileGap}>
          {offerViews.map(view => <HighIntentRecommendationTile {...view} key={view.offerId} />)}
        </CardCarousel>
      </VerticalSpacer>
    </LayoutContainer>
  </Section>
}

function mapStateToProps(state: App.State): MappedProps {
  return {
    offers: getRecentlyViewedOffers(state),
    recentlyViewedOfferIds: getRecentlyViewedOfferIds(state),
    isRecentlyViewedLoading: isRecentlyViewedOffersLoading(state),
    defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(state),
  }
}

export default connect(mapStateToProps)(RecentlyViewedOffersCarousel)
